// The dashboard's mood pair (W57.0) — two independent cards, one row.
//
// W47.0 shipped this as **one** saturated card with two columns in it. The
// reason it is now two cards is in `MoodCard.tsx`'s docstring; the short
// version is that a column at 320px was ~85px wide and the text in it was
// being cut by 46% (`ADR-0134`).
//
// This component owns the **grid** and the **naming**; `MoodCard` owns one
// person's card. Splitting it that way is what lets the two sides be
// genuinely independent: the viewer's side is a button, the partner's is not,
// and nothing about one is a variant of the other.
//
// ## The names
//
// The viewer's side says 「我」 and the partner's says their **display name**.
// The same rule the activity feed uses (`actorName` in `dashboardHelpers.ts`),
// and for the same reason: you know which one is you without being told, and
// you do not always remember your partner's name at a glance — so the screen
// tells you.

import type { DashboardActivity, DashboardMoods } from "@/lib/api/dashboard";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import { MoodCard } from "./MoodCard";
import type { PairSides } from "./dashboardHelpers";

export interface MoodPairProps {
  moods: DashboardMoods;
  activity: DashboardActivity;
  /** The pair's two members, resolved **against the viewer** (W57.0). */
  sides: PairSides;
  /** Opens the mood picker. Only the viewer's own side receives it. */
  onPickMood: () => void;
}

export function MoodPair({ moods, activity, sides, onPickMood }: MoodPairProps) {
  return (
    <section
      data-testid="dashboard-mood-pair"
      // The section names itself for assistive technology. There is no visible
      // heading: the two cards are headed by two people, and the emoji beside
      // each mood label says what they are. A visible 「今天心情」 heading would
      // be a third line between two cards that already say it.
      aria-label={DASHBOARD_I18N.mood.label}
      // W57.0 quick fix — **no `items-start`**. The grid's default
      // `align-items: stretch` is what makes the two cards the **same height**,
      // and `items-start` was exactly what let the card with a two-line
      // activity line grow taller than the one with a single line.
      //
      // The card is the grid item, so it stretches to the row's height; the
      // activity line inside it is bottom-anchored (`mt-auto`, in `MoodCard`),
      // so the two activity lines also start at the same y — otherwise the
      // shorter card's line would float in the middle of a card with space
      // under it.
      className="grid grid-cols-2 gap-s-3"
    >
      <MoodCard
        side="me"
        name={sides.me?.display_name ?? DASHBOARD_I18N.modules.actorMe}
        heading={DASHBOARD_I18N.modules.actorMe}
        avatar={sides.me?.avatar_url ?? null}
        moodCode={moods.me}
        activity={activity.me}
        onPickMood={onPickMood}
      />
      <MoodCard
        side="partner"
        name={sides.partner?.display_name ?? DASHBOARD_I18N.modules.actorPartner}
        heading={sides.partner?.display_name ?? DASHBOARD_I18N.modules.actorPartner}
        avatar={sides.partner?.avatar_url ?? null}
        moodCode={moods.partner}
        activity={activity.partner}
      />
    </section>
  );
}
